import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState('');
  const [ssoError, setSsoError] = useState('');

  const { login, forgotPassword, error, clearError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/';

  // Handle SSO callback with token
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const isNewUser = urlParams.get('new');
    const provider = urlParams.get('provider');
    const ssoError = urlParams.get('error');

    console.log('🔍 Login page useEffect running');
    console.log('URL params:', { token: token ? 'present' : 'none', isNewUser, provider, ssoError });

    if (ssoError) {
      // Handle SSO error
      clearError();
      // Show error message based on error code
      const errorMessages = {
        'google_auth_failed': 'Google authentication failed. Please try again.',
        'microsoft_auth_failed': 'Microsoft authentication failed. Please try again.',
        'auto_register_disabled': 'Auto-registration is disabled. Please contact your administrator.',
        'domain_not_allowed': 'Your email domain is not allowed. Please use an authorized email address.',
        'Google SSO is not configured': '⚠️ Google SSO is not configured yet. Please contact your administrator to set up Google OAuth credentials.',
        'Microsoft SSO is not configured': '⚠️ Microsoft SSO is not configured yet. Please contact your administrator to set up Microsoft OAuth credentials.',
      };
      const errorMsg = errorMessages[ssoError] || decodeURIComponent(ssoError);
      setSsoError(errorMsg);

      // Clear URL parameter
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    if (token) {
      console.log('✅ Token found, storing in localStorage');
      // Store token in localStorage
      localStorage.setItem('authToken', token);
      console.log('Token stored, reloading to authenticate');

      // Use window.location to force a full page reload
      // This ensures AuthContext validates the token before navigation
      const redirectPath = isNewUser === 'true' ? '/dashboard' : (from || '/');
      console.log('Redirecting to:', redirectPath);

      // Force full page reload to let AuthContext validate the token
      window.location.href = redirectPath;
    } else {
      console.log('❌ No token found in URL');
    }
  }, [navigate, from, clearError]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) clearError();
    if (ssoError) setSsoError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await login(formData);
      navigate(from, { replace: true });
    } catch (error) {
      console.error('Login failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await forgotPassword(forgotPasswordEmail);
      setForgotPasswordMessage('Password reset instructions have been sent to your email.');
      setForgotPasswordEmail('');
    } catch (error) {
      setForgotPasswordMessage('Failed to send password reset email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // SSO Handlers
  const handleGoogleSSO = () => {
    window.location.href = '/auth/google';
  };

  const handleMicrosoftSSO = () => {
    window.location.href = '/auth/microsoft';
  };

  if (showForgotPassword) {
    return (
      <div 
        data-testid="forgot-password-page"
        className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8"
      >
        <div className="max-w-md w-full space-y-10">
          <div data-testid="forgot-password-header">
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Reset your password
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Enter your email address and we'll send you a link to reset your password.
            </p>
          </div>
          
          <form 
            data-testid="forgot-password-form"
            className="mt-8 space-y-8" 
            onSubmit={handleForgotPassword}
          >
            <div>
              <label htmlFor="forgot-email" className="sr-only">
                Email address
              </label>
              <input
                id="forgot-email"
                name="email"
                type="email"
                required
                data-testid="forgot-password-email-input"
                className="form-input"
                placeholder="Email address"
                value={forgotPasswordEmail}
                onChange={(e) => setForgotPasswordEmail(e.target.value)}
              />
            </div>

            {forgotPasswordMessage && (
              <div 
                data-testid="forgot-password-message"
                className={`p-3 rounded-md ${
                  forgotPasswordMessage.includes('sent') 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-red-100 text-red-700'
                }`}
              >
                {forgotPasswordMessage}
              </div>
            )}

            <div className="flex flex-col space-y-3">
              <button
                type="submit"
                disabled={isLoading}
                data-testid="send-reset-link-button"
                className="btn-primary w-full"
              >
                {isLoading ? 'Sending...' : 'Send Reset Link'}
              </button>
              
              <button
                type="button"
                data-testid="back-to-login-button"
                onClick={() => {
                  setShowForgotPassword(false);
                  setForgotPasswordMessage('');
                }}
                className="btn-secondary w-full"
              >
                Back to Login
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div 
      data-testid="login-page"
      className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8"
    >
      <div className="max-w-md w-full space-y-10">
        <div data-testid="login-header">
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Sign in to your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            UK HR & Payroll Management System
          </p>
        </div>
        
        <form 
          data-testid="login-form" 
          className="mt-8 space-y-8" 
          onSubmit={handleSubmit}
        >
          <div className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                data-testid="email-input"
                className="form-input mt-1"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleChange}
              />
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                data-testid="password-input"
                className="form-input mt-1"
                placeholder="Enter your password"
                value={formData.password}
                onChange={handleChange}
              />
            </div>
          </div>

          {error && (
            <div
              data-testid="login-error"
              className="p-3 bg-red-100 border border-red-400 text-red-700 rounded-md"
            >
              {error}
            </div>
          )}

          {ssoError && (
            <div
              data-testid="sso-error"
              className="p-3 bg-orange-100 border border-orange-400 text-orange-700 rounded-md"
            >
              {ssoError}
            </div>
          )}

          <div className="flex items-center justify-between">
            <button
              type="button"
              data-testid="forgot-password-link"
              onClick={() => setShowForgotPassword(true)}
              className="text-sm text-primary-600 hover:text-primary-500"
            >
              Forgot your password?
            </button>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            data-testid="login-submit"
            className="btn-primary w-full"
          >
            {isLoading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>

        {/* SSO Divider */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-gray-50 text-gray-500">Or continue with</span>
          </div>
        </div>

        {/* SSO Buttons */}
        <div className="grid grid-cols-1 gap-3">
          {/* Google SSO Button */}
          <button
            type="button"
            onClick={handleGoogleSSO}
            className="w-full inline-flex items-center justify-center px-4 py-2.5 border border-gray-300 rounded-lg shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Sign in with Google
          </button>

          {/* Microsoft SSO Button */}
          <button
            type="button"
            onClick={handleMicrosoftSSO}
            className="w-full inline-flex items-center justify-center px-4 py-2.5 border border-gray-300 rounded-lg shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 23 23">
              <path fill="#f3f3f3" d="M0 0h23v23H0z"/>
              <path fill="#f35325" d="M1 1h10v10H1z"/>
              <path fill="#81bc06" d="M12 1h10v10H12z"/>
              <path fill="#05a6f0" d="M1 12h10v10H1z"/>
              <path fill="#ffba08" d="M12 12h10v10H12z"/>
            </svg>
            Sign in with Microsoft
          </button>
        </div>

        {/* Help Text */}
        <p className="text-center text-xs text-gray-500 mt-4">
          By signing in, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  );
};

export default Login;